// GoldSrc sprite (.spr) parser — used for effects (muzzle flash, smoke).
// Decodes frame 0 to RGBA. Framework-agnostic.

export interface Spr {
  width: number
  height: number
  rgba: Uint8Array
  /** texFormat: 0 normal, 1 additive, 2 index-alpha, 3 alpha-test. */
  additive: boolean
}

export function parseSpr(buffer: ArrayBuffer): Spr {
  const dv = new DataView(buffer)
  const ident = String.fromCharCode(
    dv.getUint8(0),
    dv.getUint8(1),
    dv.getUint8(2),
    dv.getUint8(3),
  )
  if (ident !== 'IDSP') throw new Error(`Not a sprite (ident "${ident}")`)

  const texFormat = dv.getInt32(12, true) // 0 normal,1 add,2 idxalpha,3 alphatest
  // header is 40 bytes, then uint16 palette count + palette*3
  const palCount = dv.getUint16(40, true)
  const palBase = 42
  const frameBase = palBase + palCount * 3

  // Frame 0 header: group, originX, originY, width, height (5 × int32).
  const width = dv.getInt32(frameBase + 12, true)
  const height = dv.getInt32(frameBase + 16, true)
  const pixelBase = frameBase + 20

  const additive = texFormat === 1
  const alphaTest = texFormat === 3
  const indexAlpha = texFormat === 2
  const pixels = width * height
  const rgba = new Uint8Array(pixels * 4)

  for (let p = 0; p < pixels; p++) {
    const idx = dv.getUint8(pixelBase + p)
    const o = p * 4
    if (indexAlpha) {
      // colour = last palette entry, alpha = index
      const pal = palBase + 255 * 3
      rgba[o] = dv.getUint8(pal)
      rgba[o + 1] = dv.getUint8(pal + 1)
      rgba[o + 2] = dv.getUint8(pal + 2)
      rgba[o + 3] = idx
      continue
    }
    const pal = palBase + idx * 3
    const r = dv.getUint8(pal)
    const g = dv.getUint8(pal + 1)
    const b = dv.getUint8(pal + 2)
    rgba[o] = r
    rgba[o + 1] = g
    rgba[o + 2] = b
    if (alphaTest) rgba[o + 3] = idx === 255 ? 0 : 255
    else if (additive)
      rgba[o + 3] = Math.max(r, g, b) // black → transparent
    else rgba[o + 3] = 255
  }

  return { width, height, rgba, additive }
}
